import React, { useState } from 'react';
import ArcPilotLogo from './ArcPilotLogo';
import DreamDevLogo from './DreamDevLogo';
import '../styles/LoginPage.css';

// Unauthenticated gate. Collects a username + password, POSTs them to
// /api/login, and on a 200 calls onAuthenticated exactly once with the name
// that was entered (093: the app shows it against work this person adds; the
// password is never handed on). On any non-200
// it surfaces an error and leaves the user on the login page. The password
// lives only in component state long enough to be POSTed; the success signal is
// purely the server's status — the client never sees the expected credential.
//
// Presentation is a branded, centred card (spec 001-login-redesign): the
// ArcPilot mark + "by DreamDev" lockup above a sign-in heading. The brand row
// reuses ArcPilotLogo + DreamDevLogo directly rather than AppBrand, because
// AppBrand wraps them in a router Link and this gate renders outside the router.
// None of the logic, state, fetch contract, or accessibility hooks below have
// changed — only the markup and class names.

type LoginPageProps = {
  onAuthenticated: (username: string) => void;
};

const LoginPage: React.FC<LoginPageProps> = ({ onAuthenticated }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (response.ok) {
        onAuthenticated(username.trim());
        return;
      }
      setError('Invalid username or password.');
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={handleSubmit}>
        <div className="login-brand">
          <ArcPilotLogo variant="dark" height={24} className="login-brand-mark" />
          <span className="login-brand-name">ArcPilot</span>
          <span className="login-brand-by">by</span>
          <DreamDevLogo
            variant="lockup"
            height={12}
            color="var(--dd-brand)"
            className="login-brand-lockup"
          />
        </div>
        <h1 className="login-title">Sign in</h1>
        <div className="login-field">
          <label className="login-label" htmlFor="login-username">Username</label>
          <input
            className="login-input"
            id="login-username"
            name="username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
          />
        </div>
        <div className="login-field">
          <label className="login-label" htmlFor="login-password">Password</label>
          <input
            className="login-input"
            id="login-password"
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </div>
        {error && <p className="login-error" role="alert">{error}</p>}
        <button className="login-submit" type="submit" disabled={submitting}>
          Log in
        </button>
      </form>
    </div>
  );
};

export default LoginPage;
